import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState, Field } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, CalendarClock, FileCheck2, Link2, CheckCircle2 } from "lucide-react";

const KINDS = [
  ["meeting", "Meeting with the Campus Coordinator", CalendarClock],
  ["paperwork", "Complete paperwork", FileCheck2],
  ["link", "Watch or read something", Link2],
  ["acknowledge", "Acknowledge / mark done", CheckCircle2],
];
const DISCIPLINES = ["PT", "OT", "SLP", "PTA", "OTA"];
const ANCHORS = [
  ["immediate", "Straight away (no date)"],
  ["rotation_start", "Rotation start date"],
  ["rotation_end", "Rotation end date"],
  ["graduation", "Graduation date"],
];
const ANCHOR_WORD = { rotation_start: "rotation start", rotation_end: "rotation end", graduation: "graduation" };
const kindMeta = (k) => KINDS.find((x) => x[0] === k) || KINDS[3];

const fmtDate = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export function offsetLabel(days, anchor) {
  const d = Number(days || 0);
  const a = anchor || (d ? "rotation_start" : "immediate");
  if (a === "immediate") return "Opens immediately";
  const word = ANCHOR_WORD[a] || "rotation start";
  if (!d) return `Opens on the ${word} date`;
  return `Opens ${Math.abs(d)} day${Math.abs(d) === 1 ? "" : "s"} ${d > 0 ? "after" : "before"} ${word}`;
}

export default function StudentTasks() {
  const [tab, setTab] = useState("checklist");
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState([]);
  const [schools, setSchools] = useState([]);
  const [levels, setLevels] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/task-templates").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/option-lists").then(({ data }) => setLevels((data.lists?.rotation_levels || []).map((o) => o.label))).catch(() => {});
    api.get("/schools").then(({ data }) => setSchools(data)).catch(() => {});
    api.get("/student-task-progress").then(({ data }) => setProgress(data)).catch(() => {});
  }, []);

  const move = async (i, dir) => {
    const next = [...rows];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
    try { await api.post("/task-templates/reorder", { ids: next.map((r) => r.id) }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const toggleActive = async (t) => {
    try { await api.patch(`/task-templates/${t.id}`, { ...t, active: !t.active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (t) => {
    try {
      const { data } = await api.delete(`/task-templates/${t.id}`);
      toast.success(data.retired ? data.reason : "Task removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Student Tasks" subtitle="The checklist every student works through, in order, from their first login">
        <Button onClick={() => setEditing({ kind: "acknowledge", disciplines: [], school_ids: [], rotation_levels: [], active: true, offset_days: 0, anchor: "immediate", standalone: false, nudge_every_days: 3 })}
          className="bg-cc-blue text-white" data-testid="new-task-btn"><Plus className="w-4 h-4 mr-1" />New Task</Button>
      </PageHeader>

      <div className="flex gap-2 mb-4" data-testid="task-tabs">
        {[["checklist", "Checklist"], ["progress", "Student progress"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`task-tab-${k}`}
            className={`px-3 py-1.5 rounded-md text-sm ${tab === k ? "bg-cc-blue text-white" : "bg-white border text-zinc-600 hover:border-cc-blue"}`}>{label}</button>
        ))}
      </div>

      {tab === "checklist" ? (
        <Card>
          {rows.length === 0 ? <EmptyState text="No tasks yet. Add the first thing a student should do." /> : (
            <div className="space-y-2" data-testid="task-template-list">
              {rows.map((t, i) => {
                const [, kindLabel, Icon] = kindMeta(t.kind);
                return (
                  <div key={t.id} className={`border rounded-md p-4 flex flex-wrap items-start gap-3 ${t.active ? "" : "opacity-60"}`} data-testid={`task-template-${t.id}`}>
                    <div className="flex flex-col gap-1 pt-0.5">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="text-zinc-300 hover:text-cc-blue disabled:opacity-30" data-testid={`task-up-${t.id}`}><ArrowUp className="w-4 h-4" /></button>
                      <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="text-zinc-300 hover:text-cc-blue disabled:opacity-30" data-testid={`task-down-${t.id}`}><ArrowDown className="w-4 h-4" /></button>
                    </div>
                    <span className="w-6 h-6 rounded-full bg-cc-blue/10 text-cc-blue text-xs flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                    <div className="flex-1 min-w-[60%]">
                      <p className="text-sm font-medium text-zinc-900">{t.title}</p>
                      <p className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5"><Icon className="w-3.5 h-3.5" />{kindLabel}</p>
                      {t.description && <p className="text-xs text-zinc-500 mt-1">{t.description}</p>}
                      {t.link_url && <a href={t.link_url} target="_blank" rel="noreferrer" className="text-xs text-cc-blue hover:underline">{t.link_url}</a>}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(t.disciplines || []).map((d) => <span key={d} className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600 rounded">{d} only</span>)}
                        {(t.school_ids || []).map((sid) => <span key={sid} className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600 rounded">{schools.find((s) => s.id === sid)?.name || "school"} only</span>)}
                        {!(t.disciplines || []).length && !(t.school_ids || []).length && <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-500 rounded">All students</span>}
                        <span className="text-[10px] px-1.5 py-0.5 bg-cc-blue/10 text-cc-blue rounded">{offsetLabel(t.offset_days, t.anchor)}</span>
                        {(t.rotation_levels || []).map((r) => <span key={r} className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded">{r} only</span>)}
                        {t.standalone && <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600 rounded">Stands alone</span>}
                        <span className="text-[10px] text-zinc-400 px-1">{t.completed_count || 0}/{t.assigned_count || 0} students done</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 basis-full sm:basis-auto justify-end sm:justify-start border-t sm:border-0 pt-3 sm:pt-0">
                      <div className="flex items-center gap-1.5">
                        <Switch checked={t.active !== false} onCheckedChange={() => toggleActive(t)} data-testid={`task-active-${t.id}`} />
                        <span className="text-xs text-zinc-400">{t.active !== false ? "Live" : "Off"}</span>
                      </div>
                      <button onClick={() => setEditing(t)} className="text-zinc-400 hover:text-cc-blue" data-testid={`task-edit-${t.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(t)} className="text-zinc-400 hover:text-red-600" data-testid={`task-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      ) : (
        <Card className="overflow-hidden">
          {progress.length === 0 ? <EmptyState text="No students yet." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                  <tr><th className="text-left px-5 py-3">Student</th><th className="text-left px-5 py-3">School</th>
                    <th className="text-left px-5 py-3">Coordinator</th><th className="text-left px-5 py-3">Progress</th>
                    <th className="text-left px-5 py-3">Next up</th></tr>
                </thead>
                <tbody className="divide-y" data-testid="task-progress-rows">
                  {progress.map((s) => (
                    <tr key={s.student_id} data-testid={`task-progress-${s.student_id}`}>
                      <td className="px-5 py-3 font-medium text-zinc-800">{s.name}<span className="text-xs text-zinc-400 ml-1.5">{s.discipline}</span></td>
                      <td className="px-5 py-3 text-zinc-600">{s.school_name || "—"}</td>
                      <td className="px-5 py-3 text-zinc-600">{s.coordinator_name || "—"}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-24 bg-zinc-100 rounded-full overflow-hidden">
                            <div className="h-full bg-cc-green" style={{ width: `${s.total ? (s.completed / s.total) * 100 : 0}%` }} />
                          </div>
                          <span className="text-xs text-zinc-500">{s.completed}/{s.total}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-zinc-700 text-xs">{s.next_task || "All done"}</p>
                        <StatusBadge status={s.next_status} className="mt-1" />
                        {s.next_available_on && <p className="text-[11px] text-zinc-400 mt-1" data-testid={`task-next-date-${s.student_id}`}>Opens {fmtDate(s.next_available_on)}</p>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {editing && <TaskDialog task={editing} schools={schools} levels={levels} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TaskDialog({ task, schools, levels, onClose, onSaved }) {
  const [form, setForm] = useState({ disciplines: [], school_ids: [], rotation_levels: [], anchor: task?.anchor || (task?.offset_days ? "rotation_start" : "immediate"), nudge_every_days: 3, ...task });
  const [saving, setSaving] = useState(false);

  const toggle = (key, value) => {
    const list = form[key] || [];
    setForm({ ...form, [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.patch(`/task-templates/${form.id}`, form);
      else await api.post("/task-templates", form);
      toast.success("Task saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto cc-scroll">
        <DialogHeader><DialogTitle>{form.id ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="What should the student do?">
            <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Set up your welcome meeting with your Campus Coordinator" data-testid="task-title" />
          </Field>
          <Field label="Task type">
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="task-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {form.kind === "link" && (
            <Field label="Link">
              <Input value={form.link_url || ""} onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                placeholder="https://…" data-testid="task-link" />
            </Field>
          )}
          <Field label="When does it open?">
            <Select value={form.anchor || "immediate"} onValueChange={(v) => setForm({ ...form, anchor: v })}>
              <SelectTrigger data-testid="task-anchor"><SelectValue /></SelectTrigger>
              <SelectContent>{ANCHORS.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent>
            </Select>
            {form.anchor !== "immediate" && (
              <div className="flex items-center gap-2 mt-2">
                <Input type="number" className="w-28" value={form.offset_days ?? 0}
                  onChange={(e) => setForm({ ...form, offset_days: e.target.value })} data-testid="task-offset" />
                <span className="text-sm text-zinc-500">days from the {ANCHOR_WORD[form.anchor]} date</span>
              </div>
            )}
            <p className="text-xs text-zinc-400 mt-1">
              {form.anchor === "immediate"
                ? "Opens on their first login."
                : `0 = the day of. Use a negative number to open before it — e.g. -21 with "rotation end" is three weeks before their rotation finishes (mock interview), or -7 with "graduation".`}
            </p>
            <p className="text-xs text-cc-blue mt-1.5 font-medium">{offsetLabel(form.offset_days, form.anchor)}</p>
          </Field>
          <Field label="Only for these rotation levels (leave empty for all)">
            <div className="flex flex-wrap gap-3" data-testid="task-levels">
              {(levels || []).map((l) => (
                <label key={l} className="flex items-center gap-1.5 text-sm text-zinc-600">
                  <Checkbox checked={(form.rotation_levels || []).includes(l)} onCheckedChange={() => toggle("rotation_levels", l)} data-testid={`task-level-${l}`} />{l}
                </label>
              ))}
              {(levels || []).length === 0 && <p className="text-xs text-zinc-400">No rotation levels configured yet (Lists &amp; Options).</p>}
            </div>
          </Field>
          <div className="flex items-start gap-2.5 border rounded-md p-3">
            <Switch checked={!!form.standalone} onCheckedChange={(v) => setForm({ ...form, standalone: v })} data-testid="task-standalone" />
            <div>
              <p className="text-sm font-medium text-zinc-800">Stands on its own</p>
              <p className="text-xs text-zinc-500">It opens purely on its date and is never blocked by an unfinished task above it (use this for the mock interview and the graduation video).</p>
            </div>
          </div>
          {form.kind === "meeting" && (
            <Field label="Chase them if they haven't booked it">
              <div className="flex items-center gap-2">
                <Input type="number" className="w-24" value={form.nudge_every_days ?? 3}
                  onChange={(e) => setForm({ ...form, nudge_every_days: e.target.value })} data-testid="task-nudge" />
                <span className="text-sm text-zinc-500">days between reminders (0 = never)</span>
              </div>
              <p className="text-xs text-zinc-400 mt-1">A dismissible pop-up shows on their dashboard every login, plus an email on this cadence until it's booked.</p>
            </Field>
          )}
          <Field label="Instructions (optional)">
            <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-description" />
          </Field>
          <Field label="Only for these disciplines (leave empty for all)">
            <div className="flex flex-wrap gap-3" data-testid="task-disciplines">
              {DISCIPLINES.map((d) => (
                <label key={d} className="flex items-center gap-1.5 text-xs text-zinc-600">
                  <Checkbox checked={(form.disciplines || []).includes(d)} onCheckedChange={() => toggle("disciplines", d)} data-testid={`task-disc-${d}`} />{d}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Only for these schools (leave empty for all)">
            <div className="border rounded-md p-2 max-h-32 overflow-y-auto cc-scroll space-y-1" data-testid="task-schools">
              {schools.length === 0 ? <p className="text-xs text-zinc-400">No schools yet.</p> : schools.map((s) => (
                <label key={s.id} className="flex items-center gap-1.5 text-xs text-zinc-600">
                  <Checkbox checked={(form.school_ids || []).includes(s.id)} onCheckedChange={() => toggle("school_ids", s.id)} data-testid={`task-school-${s.id}`} />{s.name}
                </label>
              ))}
            </div>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-cc-blue text-white" data-testid="save-task-btn">{saving ? "Saving…" : "Save task"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
